import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useScenario } from '../context/ScenarioContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { LayoutDashboard, TrendingUp, Boxes, Factory, Wallet, Users, Network, Megaphone, ClipboardList, FileBarChart, Menu, LogOut, User, ChevronDown, ChevronRight, Settings, Building2, SlidersHorizontal, Layers, Search, HelpCircle, Landmark, CalendarRange, BookOpenCheck, BarChart3 } from 'lucide-react';

const NAV = [
  { group: null, items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
  { group: 'Ricavi', items: [
    { to: '/ricavi', label: 'Ricavi', icon: TrendingUp },
    { to: '/ricavi/mix-installatori', label: 'Mix Installatori', icon: Boxes },
    { to: '/ricavi/mix-impianti', label: 'Mix Impianti', icon: Boxes },
    { to: '/ricavi/produzione', label: 'Produzione EU/Asia', icon: Factory },
  ] },
  { group: 'Costi', items: [
    { to: '/costi', label: 'Costi', icon: Wallet },
  ] },
  { group: 'Pianificazione', items: [
    { to: '/pianificazione/piani', label: 'Piani', icon: FileBarChart },
  ] },
  { group: 'Analisi CG (Avanzata)', items: [
    { to: '/analisi-cg/piano-conti', label: 'Piano dei conti', icon: BookOpenCheck },
    { to: '/analisi-cg/periodi', label: 'Bilanci Fier S.p.A.', icon: Landmark },
    { to: '/analisi-cg/kpi-conti', label: 'KPI conti', icon: BarChart3 },
  ] },
  { group: 'Sistema', items: [
    { to: '/impostazioni', label: 'Impostazioni', icon: Settings },
  ] },
];

const LABELS = NAV.flatMap((g) => g.items).reduce((acc, i) => { acc[i.to] = i.label; return acc; }, {});

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b">
        <div className="h-9 w-9 rounded-lg bg-black text-white grid place-items-center font-bold text-sm tracking-tight">FH</div>
        <div className="leading-tight">
          <div className="font-bold text-sm text-black tracking-tight">HOLDING.FIER</div>
          <div className="text-[11px] text-muted-foreground">Piano Industriale</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid="sidebar-nav">
        {NAV.map((section, idx) => (
          <div key={idx} className="space-y-1">
            {section.group && <div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{section.group}</div>}
            {section.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/ricavi' || item.to === '/costi'} onClick={onNavigate}
                className={({ isActive }) => `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-[hsl(var(--sidebar-active))] text-[hsl(var(--sidebar-active-foreground))] font-medium' : 'text-foreground/80 hover:bg-[hsl(var(--sidebar-active))]/60'}`}
                data-testid="sidebar-nav-item">
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-5 py-3 border-t text-[11px] text-muted-foreground">v1.0 · Accesso riservato</div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { active } = useScenario();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const pageLabel = LABELS[location.pathname] || 'Dashboard';
  const groupOf = NAV.find((g) => g.items.some((i) => location.pathname.startsWith(i.to) && i.to !== '/dashboard'))?.group;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-[268px] flex-col bg-[hsl(var(--sidebar))] border-r z-40">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[268px] bg-[hsl(var(--sidebar))]"><SidebarContent onNavigate={() => setMobileOpen(false)} /></SheetContent>
      </Sheet>

      <div className="lg:pl-[268px]">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-background/90 backdrop-blur border-b flex items-center gap-3 px-4 md:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="sidebar-collapse-toggle"><Menu className="h-5 w-5" /></Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="top-header-breadcrumb">
              <span className="font-bold text-black">HOLDING.FIER</span>
              {groupOf && <><ChevronRight className="h-3 w-3" /><span>{groupOf}</span></>}
              <ChevronRight className="h-3 w-3" /><span className="text-foreground font-medium">{pageLabel}</span>
            </div>
            <h2 className="text-sm font-semibold text-foreground truncate">{pageLabel}</h2>
          </div>
          {active && (
            <Badge variant="outline" className="hidden sm:inline-flex bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-[hsl(var(--border))] font-medium" data-testid="top-header-active-plan-badge">
              Piano: {active.name} · {active.year}
            </Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="top-header-user-menu">
                <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-[11px] font-semibold">{(user?.name || user?.email || 'U')[0].toUpperCase()}</div>
                <span className="hidden md:inline text-xs max-w-[140px] truncate">{user?.name || user?.email}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                <Badge variant="secondary" className="mt-1.5 text-[10px]">{user?.role === 'admin' ? 'Direzione' : 'Controllo di Gestione'}</Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }} data-testid="top-header-logout" className="text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 md:p-6 max-w-[1600px] mx-auto fade-in">{children}</main>
      </div>
    </div>
  );
}
